import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { vendedoresService } from '@/services/vendedoresService';
import { isValidCPF, isValidCNPJ, formatCNPJ } from '@/utils/cpfValidation';
import { useEstados, useCidades } from '@/hooks/useParceiros';
import { Loader2, FileText } from 'lucide-react';
import { VendedorDetailedMetrics } from '@/types/vendedores';

// Funções de formatação
const formatTelefone = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const formatCPF = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return value;
};

interface EditarVendedorProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendedor: VendedorDetailedMetrics | null;
}

type TipoPessoa = 'PF' | 'PJ';

export function EditarVendedor({ open, onClose, onSuccess, vendedor }: EditarVendedorProps) {
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('PF');
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    pix: '',
    estado: '',
    cidade: '',
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    cpf_representante: ''
  });
  const [loading, setLoading] = useState(false);

  const { estados, loading: estadosLoading } = useEstados();
  const { cidades, loading: cidadesLoading } = useCidades(formData.estado);
  const [loadingCidadeValue, setLoadingCidadeValue] = useState(false);

  // Preencher formulário com dados do vendedor quando abrir
  useEffect(() => {
    if (vendedor && open) {
      setLoadingCidadeValue(true);
      setTipoPessoa((vendedor.vendedor_tipo_pessoa === 'PJ' ? 'PJ' : 'PF') as TipoPessoa);
      setFormData({
        nome: vendedor.vendedor_nome || '',
        telefone: vendedor.vendedor_telefone || '',
        cpf: vendedor.vendedor_cpf || '',
        pix: vendedor.vendedor_pix || '',
        cidade: '',
        estado: vendedor.vendedor_estado || '',
        cnpj: vendedor.vendedor_cnpj || '',
        razao_social: vendedor.vendedor_razao_social || '',
        nome_fantasia: vendedor.vendedor_nome_fantasia || '',
        cpf_representante: vendedor.vendedor_cpf_representante || ''
      });
    }
  }, [vendedor, open]);

  // Setar cidade após as cidades serem carregadas
  useEffect(() => {
    if (loadingCidadeValue && !cidadesLoading && cidades.length > 0 && vendedor?.vendedor_cidade) {
      setFormData(prev => ({ ...prev, cidade: vendedor.vendedor_cidade || '' }));
      setLoadingCidadeValue(false);
    }
  }, [loadingCidadeValue, cidadesLoading, cidades, vendedor]);

  // Limpar cidade quando estado mudar manualmente (apenas se for diferente do original)
  useEffect(() => {
    if (!loadingCidadeValue && formData.estado && vendedor && formData.estado !== vendedor.vendedor_estado) {
      setFormData(prev => ({ ...prev, cidade: '' }));
    }
  }, [formData.estado, vendedor, loadingCidadeValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação básica
    const nomeOuRazao = tipoPessoa === 'PJ' ? formData.razao_social?.trim() : formData.nome?.trim();
    if (!nomeOuRazao || !formData.telefone) {
      toast.error(tipoPessoa === 'PJ' ? 'Razão social e telefone são obrigatórios!' : 'Nome e telefone são obrigatórios!');
      return;
    }

    // Validação de estado e cidade
    if (!formData.estado || !formData.cidade) {
      toast.error('Estado e cidade são obrigatórios!');
      return;
    }

    if (!vendedor?.vendedor_id) {
      toast.error('ID do vendedor não encontrado');
      return;
    }

    if (tipoPessoa === 'PF') {
      if (formData.cpf) {
        const cpfDigits = formData.cpf.replace(/\D/g, '');
        if (cpfDigits.length !== 11 || !isValidCPF(formData.cpf)) {
          toast.error('CPF inválido.');
          return;
        }
      }
    } else {
      const cnpjDigits = formData.cnpj.replace(/\D/g, '');
      if (!cnpjDigits || cnpjDigits.length !== 14) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!isValidCNPJ(formData.cnpj)) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!formData.razao_social?.trim()) {
        toast.error('Razão social é obrigatória para Pessoa Jurídica.');
        return;
      }
      const cpfRepDigits = formData.cpf_representante.replace(/\D/g, '');
      if (!cpfRepDigits || cpfRepDigits.length !== 11 || !isValidCPF(formData.cpf_representante)) {
        toast.error('CPF do representante legal é obrigatório e inválido.');
        return;
      }
    }

    setLoading(true);

    try {
      const payload: any = {
        nome: tipoPessoa === 'PJ' ? formData.razao_social?.trim() : formData.nome,
        telefone: formData.telefone,
        pix: formData.pix || undefined,
        estado: formData.estado,
        cidade: formData.cidade,
        tipo_pessoa: tipoPessoa
      };
      if (tipoPessoa === 'PF') {
        payload.cpf = formData.cpf || undefined;
      } else {
        payload.cnpj = formData.cnpj.replace(/\D/g, '');
        payload.razao_social = formData.razao_social.trim();
        payload.nome_fantasia = formData.nome_fantasia?.trim() || undefined;
        payload.cpf_representante = formData.cpf_representante.replace(/\D/g, '');
      }
      await vendedoresService.updateVendedor(vendedor.vendedor_id, payload);

      toast.success('Vendedor atualizado com sucesso!');
      
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar vendedor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Vendedor</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Tipo de cadastro</Label>
            <Select value={tipoPessoa} onValueChange={(v: TipoPessoa) => setTipoPessoa(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Pessoa Física (CPF)</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica (CNPJ)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Dados PJ: primeiros campos do formulário (Razão Social, CNPJ, Representante Legal, CPF) */}
          {tipoPessoa === 'PJ' && (
            <>
              <div>
                <Label htmlFor="razao_social" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Razão Social *
                </Label>
                <Input
                  id="razao_social"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  placeholder="Ex: Solar Tech Ltda"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  value={formatCNPJ(formData.cnpj)}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value.replace(/\D/g, '') })}
                  placeholder="00.000.000/0000-00"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="nome_fantasia">Representante Legal *</Label>
                <Input
                  id="nome_fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                  placeholder="Nome do representante legal"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="cpf_representante">CPF do Representante Legal *</Label>
                <Input
                  id="cpf_representante"
                  value={formatCPF(formData.cpf_representante)}
                  onChange={(e) => setFormData({ ...formData, cpf_representante: e.target.value.replace(/\D/g, '') })}
                  placeholder="000.000.000-00"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}
          {tipoPessoa === 'PF' && (
            <div>
              <Label htmlFor="nome">Nome Completo *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: João Silva"
                required
                disabled={loading}
              />
            </div>
          )}

          <div>
            <Label htmlFor="email-display">Email</Label>
            <Input
              id="email-display"
              type="email"
              value={vendedor?.vendedor_email || ''}
              placeholder="Email não disponível"
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">
              O email não pode ser alterado
            </p>
          </div>

          <div>
            <Label htmlFor="telefone">Telefone *</Label>
            <Input
              id="telefone"
              value={formatTelefone(formData.telefone)}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') })}
              placeholder="(00) 00000-0000"
              required
              disabled={loading}
            />
          </div>

          {tipoPessoa === 'PF' && (
            <div>
              <Label htmlFor="cpf" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                CPF
              </Label>
              <Input
                id="cpf"
                value={formatCPF(formData.cpf)}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                placeholder="000.000.000-00"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">Campo opcional</p>
            </div>
          )}

          <div>
            <Label htmlFor="pix">Chave PIX</Label>
            <Input
              id="pix"
              value={formData.pix}
              onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
              placeholder="Ex: email@exemplo.com, CPF, telefone ou chave aleatória"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Campo opcional - Chave PIX para recebimento de comissões
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estado">Estado (UF) *</Label>
              <Select
                value={formData.estado}
                onValueChange={(value) => setFormData({ ...formData, estado: value })}
                disabled={loading || estadosLoading}
                required
              >
                <SelectTrigger id="estado">
                  <SelectValue placeholder={estadosLoading ? "Carregando..." : "Selecione..."} />
                </SelectTrigger>
                <SelectContent>
                  {estados.map((estado) => (
                    <SelectItem key={estado.sigla} value={estado.sigla}>
                      {estado.sigla} - {estado.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="cidade">Cidade *</Label>
              <Select
                value={formData.cidade}
                onValueChange={(value) => setFormData({ ...formData, cidade: value })}
                disabled={loading || !formData.estado || cidadesLoading}
                required
              >
                <SelectTrigger id="cidade">
                  <SelectValue 
                    placeholder={
                      !formData.estado 
                        ? "Selecione o estado primeiro" 
                        : cidadesLoading 
                          ? "Carregando cidades..." 
                          : "Selecione a cidade"
                    } 
                  />
                </SelectTrigger>
                <SelectContent>
                  {cidades.map((cidade) => (
                    <SelectItem key={cidade.id} value={cidade.municipio}>
                      {cidade.municipio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!formData.estado && (
                <p className="text-xs text-muted-foreground mt-1">
                  Selecione o estado primeiro
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

